import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { logOut } from 'actions/authentication';
import { loadBootstrap } from 'actions/main';
import { loadProvider } from 'actions/provider';
import { getTeamUsers, loadUser } from 'actions/user';
import FullScreenLoader from 'atoms/full-screen-loader';
import {
  SELECTED_GROOMER_STORAGE_KEY,
  setSelectedGroomer,
} from 'store/reducers/ui';
import { setUserLocations } from 'store/reducers/user';
import { viewAllGroomersOption } from 'util/constants';

import AppContainer from './config/AppContainer';

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    dispatch(loadBootstrap()).then(({ success }) => {
      if (!success) setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (token) {
      fetchUserData();
    }
  }, [token]);

  const fetchUserData = async () => {
    setIsLoading(true);
    const userDataCall = await dispatch(loadUser());
    if (!userDataCall.success) {
      logOut('/');
      return;
    }
    const userData = userDataCall.data.data;
    const isOwner = userData.providerUsers?.[0]?.role?.value === 'Owner';
    const puid = userData.providerUsers?.[0]?.provider?.uuid;

    if (puid) {
      const providerData = await dispatch(loadProvider(puid));
      if (providerData.success) {
        dispatch(setUserLocations(providerData.data.data.locations));

        // Get all groomers
        const getUsers = await dispatch(getTeamUsers());
        if (getUsers.success) {
          // If user isn't the owner, default them as the selected groomer.
          // Otherwise, set the selected groomer to the stored option (if existing) or "All".
          if (!isOwner) {
            dispatch(
              setSelectedGroomer({
                name: userData.firstName,
                uuid: userData.uuid,
                links: userData.links,
              })
            );
          } else {
            const storedSelectedGroomer = localStorage.getItem(
              SELECTED_GROOMER_STORAGE_KEY
            );
            const parsedSelectedGroomer = storedSelectedGroomer
              ? JSON.parse(storedSelectedGroomer)
              : null;

            const availableGroomers = getUsers?.data?.data;
            const canSetGroomer =
              parsedSelectedGroomer &&
              availableGroomers.find(
                (groomer) => groomer?.user?.uuid === parsedSelectedGroomer?.uuid
              );

            if (canSetGroomer) {
              dispatch(setSelectedGroomer(parsedSelectedGroomer));
            } else dispatch(setSelectedGroomer(viewAllGroomersOption));
          }
        }
        redirectToPage(providerData.data.data, userData);
        // For testing purposes.
        // Comment out redirectToPage function and uncomment line below to avoid redirection
        // setIsLoading(false);
      }
    } else {
      setIsLoading(false);
      navigate('/signup-2');
    }
  };

  const redirectToPage = (providerData, userData) => {
    const providerHasLocation = providerData?.locations?.[0];

    if (!userData.mfaVerified) {
      navigate('/signup/phone-verification');
    } else if (providerHasLocation) {
      if (
        location.pathname === '/' ||
        location.pathname.includes('signup') ||
        location.pathname.includes('login')
      ) {
        navigate('/appointments');
      } else {
        setIsLoading(false);
        return;
      }
    } else {
      // Provider has no locations
      navigate('/signup-2');
    }
    setIsLoading(false);
  };

  const theme = createTheme({
    typography: {
      fontFamily: ['Poppins', 'Helvetica', 'Arial', 'sans-serif'].join(','),
    },
  });

  if (isLoading) {
    return <FullScreenLoader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AppContainer />
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
